import { useState, useCallback, useMemo } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Button, Chip } from '@mui/material';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import { f } from 'src/utils/formatTime';

const statusConfig = {
  ACTIVE:   { label: 'Active',   color: 'success' },
  INACTIVE: { label: 'Inactive', color: 'warning' },
  EXPIRED:  { label: 'Expired',  color: 'error'   },
};

const typeConfig = {
  AC: { bg: '#E8F5E9', color: '#2E7D32' },
  DC: { bg: '#E3F2FD', color: '#1565C0' },
};

const baseColumns = [
  {
    field: 'chargebox_id',
    headerName: 'Charger ID',
    flex: 1.2,
    minWidth: 130,
    renderCell: ({ value }) => (
      <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 500, fontSize: 13 }}>
        {value}
      </Box>
    ),
  },
  {
    field: 'charger_type',
    headerName: 'Type',
    flex: 0.6,
    minWidth: 70,
    align: 'center',
    headerAlign: 'center',
    renderCell: ({ value }) => {
      if (!value) return <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>;
      const cfg = typeConfig[value] ?? { bg: 'grey.200', color: 'text.primary' };
      return (
        <Box sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: 12 }}>
          {value}
        </Box>
      );
    },
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 0.9,
    minWidth: 100,
    align: 'center',
    headerAlign: 'center',
    renderCell: ({ value }) => {
      if (!value) return null;
      const cfg = statusConfig[value] ?? { label: value, color: 'default' };
      return <Chip label={cfg.label} color={cfg.color} size="small" sx={{ fontWeight: 600, fontSize: 11 }} />;
    },
  },
  {
    field: 'expiry',
    headerName: 'Expiry Date',
    flex: 1,
    minWidth: 120,
    align: 'center',
    headerAlign: 'center',
    renderCell: ({ value }) => (
      <Box component="span" sx={{ fontSize: 13 }}>{value ? f(value, 'dd/MM/yyyy') : '—'}</Box>
    ),
  },
  {
    field: 'final_expiry',
    headerName: 'Final Expiry',
    flex: 1,
    minWidth: 120,
    align: 'center',
    headerAlign: 'center',
    renderCell: ({ value }) => (
      <Box component="span" sx={{ fontSize: 13, fontWeight: 500 }}>{value ? f(value, 'dd/MM/yyyy') : '—'}</Box>
    ),
  },
];

/**
 * Props:
 * - rows: array of charger objects with charge_box_pk, charger_type, chargebox_id, status, expiry, final_expiry
 * - loading: boolean
 * - onSelectionChange: (selected: object[]) => void
 * - onView: (charge_box_pk: string) => void — optional; shows a View button column when provided
 */
export const RenewalChargerTable = ({ rows = [], loading, onSelectionChange, onView }) => {
  const [selectionModel, setSelectionModel] = useState([]);

  const handleSelectionChange = useCallback(
    (newSelection) => {
      setSelectionModel(newSelection);
      const selected = rows
        .filter((row) => newSelection.includes(row.charge_box_pk))
        .map(({ charge_box_pk, charger_type, chargebox_id, status, final_expiry, expiry, license_id, license_pk }) => ({
          charge_box_pk, charger_type, chargebox_id, status, final_expiry, expiry, license_id, license_pk,
        }));
      onSelectionChange?.(selected);
    },
    [rows, onSelectionChange]
  );

  const columns = useMemo(() => {
    if (!onView) return baseColumns;
    return [
      ...baseColumns,
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 0.7,
        minWidth: 90,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Button
            variant="contained"
            size="small"
            startIcon={<Icon icon={eyeFill} />}
            onClick={() => onView(params.row.charge_box_pk)}
            sx={{ backgroundColor: '#00AB55', color: 'white', '&:hover': { backgroundColor: '#007B55' } }}
          >
            View
          </Button>
        ),
      },
    ];
  }, [onView]);

  return (
    <Box sx={{ width: '100%' }}>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.charge_box_pk}
        checkboxSelection
        disableRowSelectionOnClick
        selectionModel={selectionModel}
        onSelectionModelChange={handleSelectionChange}
        loading={loading}
        autoHeight
        pageSize={10}
        rowsPerPageOptions={[10, 20]}
        sx={(theme) => ({
          border: 'none',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50],
            borderRadius: 0,
            fontSize: 13,
            fontWeight: 600,
            color: 'text.secondary',
          },
          '& .MuiDataGrid-cell': { fontSize: 13 },
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
          '& .MuiDataGrid-row:hover': { backgroundColor: 'action.hover' },
          '& .MuiDataGrid-columnSeparator': { display: 'none' },
        })}
      />
    </Box>
  );
};
